import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ResponsiveShell from '../components/layout/ResponsiveShell'
import BotSandboxPanel from '../components/botconfig/BotSandboxPanel'
import GatesConfirmDialog from '../components/playbook/GatesConfirmDialog'
import PlaybookList from '../components/playbook/PlaybookList'
import PlaybookEditor from '../components/playbook/PlaybookEditor'
import { botConfigErrorCode, botConfigErrorDetail, RETRYABLE_BOT_CONFIG_ERRORS } from '../api/botConfig'
import useBotConfigStore from '../store/useBotConfigStore'
import useManagerStore from '../store/managerStore'

function DraftBar({ onSave, onPublish, saving, dirty, hasUnpublishedChanges, canWrite, canPublish }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <span className="rounded-full border border-dashed border-slate-400 px-3 py-1 font-mono text-[11px] text-slate-600">
        {dirty || hasUnpublishedChanges ? t('playbook.draft') : t('playbook.published')}
      </span>
      <button type="button" className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-40" disabled={!canWrite || !dirty || saving} onClick={onSave}>{saving ? t('playbook.saving') : t('playbook.saveDraft')}</button>
      <button type="button" className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40" disabled={!canPublish || (!dirty && !hasUnpublishedChanges) || saving} onClick={onPublish}>{t('playbook.publish')}</button>
    </div>
  )
}

function previewPlaybook(template, stored) {
  // A switched-off playbook keeps its tuned config; show it instead of the starter default.
  const source = stored ?? template?.default_config ?? template ?? {}
  return {
    id: source.id ?? template?.id,
    type: source.type ?? template?.type,
    label: source.label ?? template?.label,
    enabled: false,
    trigger: source.trigger ?? { description: '', keywords: [] },
    action: source.action ?? { kind: '' },
    confirmation: source.confirmation ?? { confirm_first: true, closing_text: '' },
    policies_text: source.policies_text ?? '',
  }
}

export default function PlaybookPage() {
  const { t, i18n } = useTranslation()
  const tenantId = useManagerStore((state) => state.tenantId)
  const [gatesWarnings, setGatesWarnings] = useState(null)
  const [selectedType, setSelectedType] = useState(null)
  const load = useBotConfigStore((state) => state.load)
  const retry = useBotConfigStore((state) => state.load)
  const togglePlaybook = useBotConfigStore((state) => state.togglePlaybook)
  const updatePlaybook = useBotConfigStore((state) => state.updatePlaybook)
  const saveDraft = useBotConfigStore((state) => state.saveDraft)
  const publishDraft = useBotConfigStore((state) => state.publishDraft)
  const loading = useBotConfigStore((state) => state.loading)
  const saving = useBotConfigStore((state) => state.saving)
  const error = useBotConfigStore((state) => state.error)
  const draft = useBotConfigStore((state) => state.draft)
  const templates = useBotConfigStore((state) => state.templates)
  const capabilities = useBotConfigStore((state) => state.capabilities)
  const limits = useBotConfigStore((state) => state.limits)
  const playbookSlots = useBotConfigStore((state) => state.playbookSlots)
  const placeholders = useBotConfigStore((state) => state.placeholders)
  const actionOptions = useBotConfigStore((state) => state.actionOptions)
  const dirty = useBotConfigStore((state) => state.dirty)
  const hasUnpublishedChanges = useBotConfigStore((state) => state.hasUnpublishedChanges)
  const conflict = useBotConfigStore((state) => state.conflict)
  const hasSandbox = Boolean(capabilities?.sandbox)

  useEffect(() => {
    if (tenantId) void load(tenantId).catch(() => {})
  }, [tenantId, load])

  const activeTypes = useMemo(
    () => draft.playbooks.filter((playbook) => playbook.enabled !== false).map((playbook) => playbook.type),
    [draft.playbooks],
  )
  const effectiveSelectedType = selectedType && templates.some((template) => template.type === selectedType)
    ? selectedType
    : activeTypes[0] ?? templates[0]?.type ?? null

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.type === effectiveSelectedType) ?? null,
    [effectiveSelectedType, templates],
  )

  const activePlaybook = useMemo(
    () => draft.playbooks.find((playbook) => playbook.enabled !== false && playbook.type === effectiveSelectedType) ?? null,
    [draft.playbooks, effectiveSelectedType],
  )
  const storedDisabled = draft.playbooks.find((playbook) => playbook.enabled === false && playbook.type === effectiveSelectedType)
  const detailPlaybook = activePlaybook ?? (selectedTemplate ? previewPlaybook(selectedTemplate, storedDisabled) : null)
  const isPreview = Boolean(!activePlaybook && detailPlaybook)
  const canWrite = Boolean(capabilities?.can_write)
  const canPublish = Boolean(capabilities?.can_publish)

  function handleToggle(template) {
    const wasActive = draft.playbooks.some((playbook) => playbook.type === template.type && playbook.enabled !== false)
    const changed = togglePlaybook(template)
    if (!changed) return

    if (!wasActive) {
      setSelectedType(template.type)
      return
    }

    const remaining = useBotConfigStore
      .getState()
      .draft.playbooks
      .find((playbook) => playbook.enabled !== false)
    setSelectedType(remaining?.type ?? null)
  }

  async function handlePublish(confirm = false) {
    try {
      await publishDraft(confirm)
    } catch (publishError) {
      if (botConfigErrorCode(publishError) === 'gates_change_unconfirmed') {
        setGatesWarnings(botConfigErrorDetail(publishError).warnings ?? [])
      }
    }
  }

  function reload() {
    if (tenantId) void retry(tenantId).catch(() => {})
  }

  let body
  if (loading && !capabilities) {
    body = <p className="py-12 text-center text-sm text-slate-500">{t('playbook.loading')}</p>
  } else if (error && !capabilities) {
    body = (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        <span>{t('playbook.loadError')}</span>
        {RETRYABLE_BOT_CONFIG_ERRORS.has(botConfigErrorCode(error)) && <button type="button" className="ml-3 font-semibold underline" onClick={reload}>{t('playbook.retry')}</button>}
      </div>
    )
  } else if (!capabilities?.can_read) {
    body = <p className="py-12 text-center text-sm text-slate-500">{t('common.unauthorized')}</p>
  } else {
    const english = i18n.language?.startsWith('en')
    const detailLabel = selectedTemplate
      ? (english ? selectedTemplate.label_en : selectedTemplate.label_vi) ?? selectedTemplate.label
      : detailPlaybook?.label ?? detailPlaybook?.type
    const canEnablePreview = isPreview && canWrite && selectedTemplate?.available !== false && activeTypes.length < (limits.max_active_playbooks ?? 3)
    body = (
      <>
        {conflict && <p role="alert" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{t('playbook.conflict')}<button type="button" className="ml-3 font-semibold underline" onClick={reload}>{t('playbook.reloadLatest')}</button></p>}
        {error && !conflict && <p role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{t('common.requestFailed')}</p>}
        <div className={`grid items-start gap-5 ${hasSandbox ? 'xl:grid-cols-[240px_minmax(0,1fr)_320px]' : 'xl:grid-cols-[240px_minmax(0,1fr)]'}`}>
          <PlaybookList
            templates={templates}
            playbooks={draft.playbooks}
            maxActive={limits.max_active_playbooks}
            canWrite={canWrite}
            selectedType={effectiveSelectedType}
            onToggle={handleToggle}
            onSelect={setSelectedType}
          />
          <section className="min-w-0">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-slate-900">{detailLabel ?? t('playbook.emptyTemplates')}</h2>
              {isPreview && <span className="rounded-full border border-dashed border-slate-400 px-2 py-1 font-mono text-[10px] text-slate-500">{t('playbook.preview')}</span>}
            </div>
            {isPreview && <button type="button" className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40" disabled={!canEnablePreview} onClick={() => handleToggle(selectedTemplate)}>{t('playbook.enable')}</button>}
          </div>
          {detailPlaybook ? (
            isPreview && <p role="status" className="mb-4 rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800">{t('playbook.previewReadOnly')}</p>
          ) : null}
          {detailPlaybook ? (
            <PlaybookEditor
              playbook={detailPlaybook}
              slotMetadata={playbookSlots}
              actionOptions={actionOptions}
              placeholders={placeholders}
              canWrite={canWrite && !isPreview}
              onChange={(changes) => updatePlaybook(detailPlaybook.type, changes)}
            />
          ) : <p className="text-sm text-slate-500">{t('playbook.subtitle')}</p>}
          {!canWrite && <p className="mt-4 rounded-lg bg-slate-100 p-3 text-sm text-slate-600">{t('common.unauthorized')}</p>}
          </section>
          {hasSandbox && <BotSandboxPanel tenantId={tenantId} />}
        </div>
      </>
    )
  }

  return (
    <ResponsiveShell title={t('playbook.title')}>
      <div className="min-h-full bg-page-bg pb-10">
        <header className="sticky top-16 z-20 mb-5 flex flex-wrap items-end justify-between gap-4 border-b border-slate-200/80 bg-page-bg/95 py-3 backdrop-blur">
          <div>
            <span className="font-mono text-xs text-slate-500">BOT / PLAYBOOK</span>
            <h1 className="text-2xl font-semibold text-slate-900">{t('playbook.title')}</h1>
            <p className="text-sm text-slate-500">{t('playbook.subtitle')}</p>
          </div>
          <DraftBar onSave={saveDraft} onPublish={() => handlePublish(false)} saving={saving} dirty={dirty} hasUnpublishedChanges={hasUnpublishedChanges} canWrite={canWrite} canPublish={canPublish} />
        </header>
        {body}
      </div>
      {gatesWarnings && <GatesConfirmDialog warnings={gatesWarnings} onConfirm={() => { setGatesWarnings(null); void handlePublish(true) }} onCancel={() => setGatesWarnings(null)} />}
    </ResponsiveShell>
  )
}
